import axios, { AxiosError } from "axios";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useLayout } from "./LayoutContext";
import { Constants } from "constants/api";
import { Loader2Icon } from "lucide-react";
import SearchableDropdown, { DropdownItem } from "components/SearchableDropdown";
import { identifyLogRocketUser } from "utils/logrocket";

interface LoginFormData {
    name: string;
    email: string;
    phone_no: string;
    age: number | null;
    gender: string | null;
    password: string;
    country_id: string | null;
    consent: boolean;
    consent_two: boolean;
}

const EdencareLogin: React.FC = () => {
    const [formData, setFormData] = useState<LoginFormData>({
        name: "",
        email: "",
        phone_no: "",
        age: null,
        gender: null,
        password: "",
        country_id: null,
        consent: false,
        consent_two: false
    });
    const navigate = useNavigate();
    const { setLayoutData } = useLayout();
    const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const rawClient = localStorage.getItem("clientDetails");
    const rawUser = localStorage.getItem("userDetails");
    const clientDetails = useMemo(() => {
        return rawClient ? JSON.parse(rawClient) : null;
    }, [rawClient]);

    const [countries, setCountries] = useState<DropdownItem[]>([]);
    const genderOptions: DropdownItem[] = [
        { label: "Male", value: "male" },
        { label: "Female", value: "female" },
    ];
    useEffect(() => {
        fetchCountries();
    }, []);

    const fetchCountries = async () => {
        try {
            const response = await axios.get(Constants.API.COUNTRIES);
            const data = response.data?.data;
            if (data) {
                //filter only id = 182
                const filteredData = data.filter((country: any) => country.id === 182);
                const formattedCountries = filteredData.map((country: any) => ({
                    label: country.name,
                    value: country.id,
                }));
                setCountries(formattedCountries);
                setFormData((prevState) => ({
                    ...prevState,
                    country_id: formattedCountries[0].value
                }));
            } else {
                setCountries([]);
            }
        } catch (error) {
            console.log('error', error);
        }
    }

    const handleCountryselect = (selectedCountry: DropdownItem) => {
        let value = selectedCountry?.value ?? null;
        setFormData((prevState) => ({
            ...prevState,
            country_id: value
        }));
    }

    const handleGenderSelect = (selectedGender: DropdownItem) => {
        let value = selectedGender?.value ?? null;
        setFormData((prevState) => ({
            ...prevState,
            gender: value
        }));
    }
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        // Numeric-only fields
        const numericFields = ["age", "phone_no"];

        if (numericFields.includes(name)) {
            // Allow only empty or digits
            if (value !== "" && !/^\d+$/.test(value)) {
                return;
            }
        }

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    useEffect(() => {
        if (rawClient) {
            setLayoutData({
                clientLogo: clientDetails?.brand_image,
                clientName: clientDetails?.client_name,
            });
        } else {
            setFormErrors({
                client_id:
                    "We couldn’t identify your client session. Please log in through the correct client URL.",
            });
        }
    }, [clientDetails, rawUser, navigate]);

    const verifyClient = useCallback(async (slugValue: string) => {
        try {
            const formdata = new FormData();
            formdata.append("slug", slugValue);

            const { data } = await axios.post(Constants.API.VERIFY_CLIENT, formdata, {
                headers: { Accept: "application/json" },
            });

            if (data?.data?.status) {
                const clientData = data.data;
                localStorage.setItem("clientDetails", JSON.stringify(clientData));
                setLayoutData({
                    clientLogo: clientData.brand_image,
                    clientName: clientData.client_name,
                });
            } else {
                localStorage.removeItem("clientDetails");
                setFormErrors({
                    client_id:
                        "We couldn’t identify your client session. Please log in through the correct client URL.",
                });
            }
        } catch (err) {
            setFormErrors({
                client_id: "Something went wrong while verifying client. Try again later.",
            });
        }
    }, []);

    useEffect(() => {
        if (clientDetails?.client_website_link) {
            verifyClient(clientDetails.client_website_link);
        } else {
            setFormErrors({
                client_id:
                    "We couldn’t identify your client session. Please log in through the correct client URL.",
            });
        }
    }, [clientDetails, verifyClient]);

    const validateForm = (clientId: number) => {
        const errors: { [key: string]: string } = {};
        if (clientId === 49) {
            //validate fields only if client id is 49
            if (!formData.name) {
                errors.name = "Name is required";
            } else if (formData.name.length < 2) {
                errors.name = "Name must be at least 2 characters long";
            }
            if (!formData.email) {
                errors.email = "Email is required";
            } else if (!isValidEmail(formData.email)) {
                errors.email = "Invalid email format";
            }
            //phone number length between 9 and 12
            if (!formData.phone_no) {
                errors.phone_no = "Phone number is required";
            } else if (formData.phone_no.length < 9 || formData.phone_no.length > 12) {
                errors.phone_no = "Phone number must be between 9 and 12 digits";
            }
            if (!formData.age) {
                errors.age = "Age is required";
            } else if (formData.age < 0) {
                errors.age = "Age must be a positive number";
            }
            if (!formData.gender) {
                errors.gender = "Gender is required";
            }
            if (!formData.country_id) {
                errors.country_id = "Country is required";
            }
            if (!formData.consent) {
                errors.consent = "Please agree to consent";
            }
            if (!formData.consent_two) {
                errors.consent_two = "Plase agree to terms and conditions";
            }
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    }

    const isValidEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }
    const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!clientDetails) {
            if (!clientDetails) {
                setFormErrors({
                    client_id:
                        "We couldn’t identify your client session. Please log in through the correct client URL.",
                });
            }
            return;
        }

        if (clientDetails && clientDetails.scan_limit <= 0) {
            setFormErrors({
                client_id:
                    "You have exceeded the scan limit for this client. Please contact your system administrator for more details.",
            })
            return;
        }
        if (!validateForm(clientDetails.id)) {
            return;
        }
        // 2. UPDATE HANDLELOGIN: Set loading to true and use a finally block
        setIsLoading(true);
        //if form data is empty just navigate to scan page
        if (!formData.name || !formData.email) {
            //reset local storage user details
            localStorage.removeItem("userDetails");
            navigate("/scan");
            return;
        }
        const loginFormData = new FormData();
        loginFormData.append("client_id", clientDetails.id);
        loginFormData.append("name", formData.name);
        loginFormData.append("email", formData.email);
        loginFormData.append("phone_no", formData.phone_no);
        loginFormData.append("age", formData.age?.toString());
        loginFormData.append("gender", formData.gender);
        loginFormData.append("country_id", formData.country_id);
        try {
            const response = await axios.post(Constants.API.LOGIN, loginFormData, {
                headers: { Accept: "application/json" },
            });

            if (response.data?.status) {
                localStorage.setItem(
                    "userDetails",
                    JSON.stringify(response.data?.user)
                );
                identifyLogRocketUser();
                localStorage.setItem("token", response.data?.token);
                navigate("/scan");
            } else {
                const errorMsg = response.data?.message || "Something went wrong";
                setFormErrors((prev) => ({ ...prev, password: errorMsg }));
            }
        } catch (error) {
            const axiosError = error as AxiosError<any>;
            const errorMsg =
                axiosError.response?.data?.message || "Something went wrong";
            setFormErrors((prev) => ({ ...prev, password: errorMsg }));
        } finally {
            // This block runs whether the try succeeds or fails
            setIsLoading(false);
        }
    };

    return (
        <div className="flex justify-center bg-gray-50 min-h-screen">

            <div className="w-full max-w-md sm:p-4 m-[5px]">
                {/* Progress Container */}
                <div className="flex items-center justify-center space-x-4 mb-4 sm:mb-4 mt-4">
                    <span className="bg-gray-200 text-gray-600 w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full text-sm sm:text-base font-medium">1</span>
                    <span className="bg-eden-primary text-white w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full text-sm sm:text-base font-medium">2</span>
                </div>
                <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
                    <h2 className="text-2xl font-bold text-gray-800 text-center">
                        User Register
                    </h2>
                    <span className="text-gray-500 text-xs block text-center mt-2">
                        Enter your email address if you'd like to receive this report by email
                    </span>
                    {formErrors.client_id && (
                        <p className="text-red-500 text-sm my-2 font-semibold text-center bg-red-50 p-3 rounded-lg">
                            {formErrors.client_id}
                        </p>
                    )}
                    <form onSubmit={handleLogin}>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700">Full Name</label>
                            <input
                                type="text"
                                placeholder="John Doe"
                                value={formData.name}
                                onChange={(e) =>
                                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                                }
                                className="mt-1 w-full px-4 py-2 border rounded-lg text-gray-700 bg-gray-50 focus:outline-none focus:ring-1 focus:ring-eden-primary focus:border-eden-primary"
                            />
                            {formErrors.name && (
                                <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>
                            )}
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700">
                                Email Address <small className="text-gray-500">(Optional)</small>
                            </label>
                            <input
                                type="email"
                                placeholder="example@domain"
                                value={formData.email}
                                onChange={(e) =>
                                    setFormData((prev) => ({ ...prev, email: e.target.value }))
                                }
                                className="mt-1 w-full px-4 py-2 border rounded-lg text-gray-700 bg-gray-50 focus:outline-none focus:ring-1 focus:ring-eden-primary focus:border-eden-primary"
                            />
                            {formErrors.email && (
                                <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>
                            )}
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                            <div className="flex mt-1">
                                <div className="flex items-center px-3 py-2 border border-r-0 rounded-l-lg bg-gray-100 text-gray-600 text-sm font-medium select-none">
                                    +250
                                </div>
                                <input
                                    type="tel"
                                    id="phone_no"
                                    name="phone_no"
                                    value={formData.phone_no}
                                    onChange={handleInputChange}
                                    className="flex-1 w-full px-4 py-2 border border-l-0 rounded-r-lg text-gray-700 bg-gray-50 focus:outline-none focus:ring-1 focus:ring-eden-primary focus:border-eden-primary"
                                />
                            </div>
                            {formErrors.phone_no && (
                                <p className="text-red-500 text-xs mt-1">{formErrors.phone_no}</p>
                            )}
                        </div>

                        {/* Age and Gender in same row for all screens */}
                        <div className="grid grid-cols-2 gap-4 mb-4">
                            <div>
                                <label htmlFor="age" className="block text-sm font-medium text-gray-700">Age</label>
                                <input
                                    type="number"
                                    id="age"
                                    name="age"
                                    value={formData.age}
                                    onChange={handleInputChange}
                                    className="mt-1 w-full px-4 py-2 border rounded-lg text-gray-700 bg-gray-50 focus:outline-none focus:ring-1 focus:ring-eden-primary focus:border-eden-primary"
                                />
                                {formErrors.age && (
                                    <p className="text-red-500 text-xs mt-1">{formErrors.age}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Gender</label>
                                <SearchableDropdown
                                    placeholder="Select Gender"
                                    items={genderOptions}
                                    value={formData.gender}
                                    onSelect={(item) => handleGenderSelect(item as DropdownItem)}
                                />
                                {formErrors.gender && (
                                    <p className="text-red-500 text-xs mt-1">{formErrors.gender}</p>
                                )}
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700">Country</label>
                            <SearchableDropdown
                                placeholder="Select country"
                                items={countries}
                                value={formData.country_id}
                                onSelect={(item) => handleCountryselect(item as DropdownItem)}
                            />
                            {formErrors.country_id && (
                                <p className="text-red-500 text-xs mt-1">{formErrors.country_id}</p>
                            )}
                        </div>

                        {/* Consent Checkboxes */}
                        <div className="mb-4 flex items-start gap-2">
                            <input
                                type="checkbox"
                                id="consent"
                                name="consent"
                                onChange={(e) => setFormData((prev) => ({ ...prev, consent: e.target.checked }))}
                                className="mt-1"
                            />
                            <label htmlFor="consent" className="text-sm text-gray-700">
                                I understand that my participation does not constitute a medical consultation or diagnosis.
                            </label>
                        </div>
                        {formErrors.consent && (
                            <p className="text-red-500 text-xs mt-1 mb-2">{formErrors.consent}</p>
                        )}

                        <div className="mb-6 flex items-start gap-2">
                            <input
                                type="checkbox"
                                id="consent_two"
                                name="consent_two"
                                onChange={(e) => setFormData((prev) => ({ ...prev, consent_two: e.target.checked }))}
                                className="mt-1"
                            />
                            <label htmlFor="consent_two" className="text-sm text-gray-700">
                                I authorize Eden Care to collect and use my anonymised data for the purpose of system evaluation and analysis.
                            </label>
                        </div>
                        {formErrors.consent_two && (
                            <p className="text-red-500 text-xs mt-1 mb-2">{formErrors.consent_two}</p>
                        )}

                        <div>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full flex justify-center items-center bg-eden-primary text-white py-3 rounded-lg text-lg font-semibold hover:bg-eden-primary/80 focus:ring-1 focus:ring-offset-1 focus:ring-eden-primary transition disabled:bg-eden-primary disabled:cursor-not-allowed"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2Icon className="animate-spin mr-2" />
                                        Loading...
                                    </>
                                ) : (
                                    "Start Scan"
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default EdencareLogin;
